import { useEffect } from "react";
import { Routes, Route, Navigate, useNavigate, useParams } from "react-router-dom";
import { useUiStore } from "./stores/ui";
import Shell from "./components/Shell";
import OAuthCallback from "./pages/OAuthCallback";
import Login from "./pages/Login";
import HomePage from "./pages/HomePage";
import AccountsPage from "./pages/AccountsPage";
import ExchangeEdit from "./pages/ExchangeEdit";
import ExchangeDetail from "./pages/ExchangeDetail";
import BacktestList from "./pages/lab/backtest/BacktestList";
import BacktestWorkspace from "./pages/lab/backtest/BacktestWorkspace";
import LiveList from "./pages/LiveList";
import LiveDetail from "./pages/LiveDetail";
import OrdersPage from "./pages/OrdersPage";
import AccountProfile from "./pages/AccountProfile";
import AccountSecurity from "./pages/AccountSecurity";
import AccountPreferences from "./pages/AccountPreferences";

function LegacyIntegrationRedirect() {
  const { id } = useParams();
  return <Navigate to={`/integrations/marketplaces/${id}`} replace />;
}

/** /lab/backtest/new opens the dialog over the list. */
function NewBacktestEntry() {
  const navigate = useNavigate();
  const open = useUiStore((s) => s.openNewBacktest);
  useEffect(() => {
    open();
    navigate("/lab/backtest", { replace: true });
  }, [open, navigate]);
  return null;
}

/** /live/new (search, onboarding, old links; ?backtest=<id> preselects one) opens the Go live dialog over the list. */
function GoLiveEntry() {
  const navigate = useNavigate();
  const open = useUiStore((s) => s.openGoLive);
  useEffect(() => {
    open(new URLSearchParams(window.location.search).get("backtest") ?? "");
    navigate("/live", { replace: true });
  }, [open, navigate]);
  return null;
}

/** Settings now live in the backtest page's properties drawer. */
function BacktestEditRedirect() {
  const { id } = useParams();
  return <Navigate to={`/lab/backtest/${id}`} replace />;
}

/** One workspace per backtest: going from one backtest straight to another starts clean, not with the other's results and chart. */
function BacktestWorkspaceRoute() {
  const { id } = useParams();
  return <BacktestWorkspace key={id} />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/oauth/callback" element={<OAuthCallback />} />

      <Route
        path="/*"
        element={
          <Shell>
            <Routes>
              <Route path="/" element={<HomePage />} />

              <Route path="/integrations/marketplaces" element={<AccountsPage />} />
              <Route path="/integrations/marketplaces/new" element={<AccountsPage openNew />} />
              <Route path="/integrations/marketplaces/:id/edit" element={<ExchangeEdit />} />
              <Route path="/integrations/marketplaces/:id" element={<ExchangeDetail />} />
              <Route path="/integrations/new" element={<Navigate to="/integrations/marketplaces/new" replace />} />
              <Route path="/integrations/:id" element={<LegacyIntegrationRedirect />} />
              <Route path="/integrations" element={<Navigate to="/integrations/marketplaces" replace />} />

              <Route path="/lab/pinescript/*" element={<Navigate to="/lab/backtest" replace />} />
              <Route path="/lab/pinescript" element={<Navigate to="/lab/backtest" replace />} />

              <Route path="/lab/backtest" element={<BacktestList />} />
              <Route path="/lab/backtest/new" element={<NewBacktestEntry />} />
              <Route path="/lab/backtest/:id/edit" element={<BacktestEditRedirect />} />
              <Route path="/lab/backtest/:id" element={<BacktestWorkspaceRoute />} />

              <Route path="/lab" element={<Navigate to="/lab/backtest" replace />} />

              <Route path="/live" element={<LiveList />} />
              <Route path="/live/new" element={<GoLiveEntry />} />
              <Route path="/live/:id" element={<LiveDetail />} />

              <Route path="/trades" element={<OrdersPage />} />
              <Route path="/orders" element={<Navigate to="/trades" replace />} />

              <Route path="/account" element={<Navigate to="/account/profile" replace />} />
              <Route path="/account/profile" element={<AccountProfile />} />
              <Route path="/account/security" element={<AccountSecurity />} />
              <Route path="/account/preferences" element={<AccountPreferences />} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Shell>
        }
      />
    </Routes>
  );
}
